import type { AnalyticsApi } from '@liks/lac-svc-api/esm/apis/AnalyticsApi';
import type {
  CostChartDayRow,
  CostChartMonthRow,
} from '../features/costs/costChartTypes';
import { dayToIsoRange, monthToIsoRange } from '../utils/dateTimeUtils';
import { toDayRow, toMonthRow } from './lacAnalyticsCostMappers';
import { LAC_ANALYTICS_PAGE_SIZE } from './lacAnalyticsServiceTypes';

/**
 * @param fromDay `YYYY-MM-DD`
 * @param toDay `YYYY-MM-DD`
 */
export async function getDailyCosts(
  analyticsApi: AnalyticsApi,
  tenantName: string,
  fromDay: string,
  toDay: string,
): Promise<CostChartDayRow[]> {
  const trimmedName = tenantName.trim();
  if (trimmedName === '') return [];

  const { fromDate, toDate } = dayToIsoRange(fromDay, toDay);
  const rows: CostChartDayRow[] = [];
  let offset = 0;

  for (;;) {
    const page =
      await analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListTokenCounterDays(
        {
          fromDate,
          toDate,
          tenants: [trimmedName],
          offset,
          limit: LAC_ANALYTICS_PAGE_SIZE,
        },
      );

    for (const row of page) {
      const mapped = toDayRow(row);
      if (mapped) rows.push(mapped);
    }

    if (page.length < LAC_ANALYTICS_PAGE_SIZE) break;
    offset += LAC_ANALYTICS_PAGE_SIZE;
  }

  return rows;
}

/**
 * @param fromMonth `YYYY-MM`
 * @param toMonth `YYYY-MM`
 */
export async function getMonthlyCosts(
  analyticsApi: AnalyticsApi,
  tenantName: string,
  fromMonth: string,
  toMonth: string,
): Promise<CostChartMonthRow[]> {
  const trimmedName = tenantName.trim();
  if (trimmedName === '') return [];

  const { fromDate, toDate } = monthToIsoRange(fromMonth, toMonth);
  const rows: CostChartMonthRow[] = [];
  let offset = 0;

  for (;;) {
    const page =
      await analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListTokenCounterMonths(
        {
          fromDate,
          toDate,
          tenants: [trimmedName],
          offset,
          limit: LAC_ANALYTICS_PAGE_SIZE,
        },
      );

    for (const row of page) {
      const mapped = toMonthRow(row);
      if (mapped) rows.push(mapped);
    }

    if (page.length < LAC_ANALYTICS_PAGE_SIZE) break;
    offset += LAC_ANALYTICS_PAGE_SIZE;
  }

  return rows;
}
